import React from 'react';
import Stack from '@mui/material/Stack';
import { Avatar, Body, Title, Theme } from '@routific/hive';
import getInitials from '../../../store/mst/utils/getInitials';

const { Spacing } = Theme;

export interface AvatarNameProps {
  /** The name to display and use for initials */
  name?: string;
  /** Size of the avatar */
  avatarSize?: 'default' | 'small' | 'large';
  /** Whether to show deleted styling (red text) */
  deleted?: boolean;
  /** Optional suffix to append to the name (e.g., "(you)") */
  suffix?: string;
  /** Optional icon to show instead of initials (e.g., for pending state) */
  icon?: React.ReactNode;
  /** Which text component to use for the name */
  textVariant?: 'body' | 'title';
  /** Optional test ID for the container */
  testId?: string;
}

const AvatarName: React.FC<AvatarNameProps> = React.memo(({
  name,
  avatarSize = 'default',
  deleted = false,
  suffix,
  icon,
  textVariant = 'body',
  testId,
}) => {
  const displayText = suffix ? `${name || ''}${suffix}` : (name || '');

  const renderText = () => {
    if (textVariant === 'title') {
      return (
        <Title level={5} ellipsis type={deleted ? 'danger' : undefined}>
          {displayText}
        </Title>
      );
    }

    return (
      <Body ellipsis type={deleted ? 'danger' : undefined}>
        {displayText}
      </Body>
    );
  };

  return (
    <Stack direction="row" alignItems="center" height="100%" data-testid={testId} data-cy={testId}>
      <Avatar
        size={avatarSize}
        style={{ marginRight: Spacing.smSpacing }}
        deleted={deleted}
        icon={icon}
      >
        {!icon && getInitials(name)}
      </Avatar>
      {renderText()}
    </Stack>
  );
});

AvatarName.displayName = 'AvatarName';

export default AvatarName;
